<template>
    <!-- Optimized Audio Recording Modal -->
    <BaseModal :title="title" @close="closeModal" :visible="true" size="medium">
        <div class="record__audio__popup">
            <div class="assesment__transcribe__wrapper popup__content__box">
                <div class="audio-overlay-message" v-show="['pending', 'processing'].includes(transcriptionStatus)">
                    <img :src="aiImg" class="animate-img" width="200" />
                    <p><b>Hang tight!</b></p>
                    <p>We’re processing your transcription<br /> Thanks for your patience, it’ll be ready shortly.</p>
                </div>
                <div class="popup-vowels-content">
                    <div class="popup-vowels-content-head">
                        <div class="rlms-flex rlms-items-center rlms-gap-5">
                            <div class="rlms-fw-500">Test Item</div>
                            <Tooltip :content="`<div class='info__options__box' style='visibility: visible;opacity:1;'>
                                        <div class='options__group light__purple'>
                                            <div class='info__colors'></div><span class='options__text'>Default</span>
                                        </div>
                                        <div class='options__group light__green'>
                                            <div class='info__colors'></div><span class='options__text'>Correct</span>
                                        </div>
                                        <div class='options__group light__red'>
                                            <div class='info__colors'></div><span class='options__text'>Incorrect</span>
                                        </div>
                                    </div>`" placement="right" width="auto">
                                <template #button>
                                    <img :src="optionsInfoIcon" />
                                </template>
                            </Tooltip>
                        </div>
                        <div class="rlms-flex rlms-items-center rlms-gap-5">
                            <input type="checkbox" v-model="noResponse" id="response-input" class="rlms-checkbox"
                                :disabled="['pending', 'processing'].includes(transcriptionStatus)"
                                @change="saveTranscribe" />
                            <label for="response-input" class="rlms-fs-xs" style="padding-left: 0px;">No
                                Response</label>
                        </div>
                    </div>
                    <div class="popup-vowels-content-body">
                        <div class="rlms-flex rlms-items-center rlms-justify-between rlms-w-100">
                            <span>{{ item.question }}</span>

                            <div class="rlms-flex rlms-gap-10 rlms-items-center rlms-relative">
                                <Tooltip :content="`${transcriptionMessage}`" placement="left"
                                    v-if="['completed'].includes(transcriptionStatus)" width="200px">
                                    <template #button>
                                        <TranscriptionFeedbackAction v-if="['completed'].includes(transcriptionStatus)"
                                            :attempt="item" :organizationId="organizationId"
                                            :assessmentId="assessmentId" @saved="handleFeedbackEvent" />
                                    </template>
                                </Tooltip>

                                <Tooltip :content="`${transcriptionMessage}`" placement="left"
                                    v-if="transcriptionStatus" width="200px"
                                    :component="['completed'].includes(transcriptionStatus) ? TranscriptionFeedbackAction : null"
                                    :componentProps="{ organizationId: organizationId, assessmentId: assessmentId, attempt: item }"
                                    @tooltipEvent="handleFeedbackEvent">
                                    <template #button>
                                        <img :src="optionsInfoIcon2" width="20" />
                                    </template>
                                </Tooltip>
                            </div>
                        </div>

                        <template v-if="consonantStates?.length && item.consonant">
                            <div class="tbh__labels__group">
                                <template v-for="(c, index) in consonantStates" :key="index">
                                    <template v-if="c.value === '-'"> - </template>
                                    <template v-else-if="c.value === ' '"> &nbsp; </template>
                                    <template v-else>
                                        <span class="label"
                                            :class="[getClassForState(c.state), { 'disabled-label': noResponse }]"
                                            @click="() => ['pending', 'processing'].includes(transcriptionStatus) || noResponse ? null : toggleConsonant(index)"
                                            :title="noResponse ? 'Disabled when No Response is checked' : ''">
                                            {{ c.value }}
                                        </span>
                                    </template>
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <div class="tbh__labels__group checkbox__group">
                                <template v-for="(c, index) in consonantStates" :key="`${index}-checkbox`">
                                    <div class="single__checkbox" :class="c">
                                        <input type="checkbox"
                                            @click="() => ['pending', 'processing'].includes(transcriptionStatus) || noResponse ? null : toggleConsonant(index)"
                                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || noResponse"
                                            :checked="c.state == 'green'" />
                                        <label></label>
                                    </div>
                                </template>
                            </div>
                        </template>
                        <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2" v-if="consonantStates?.length">
                            {{ item.consonant ? PHONEME_COLOUR_LEGEND : SYLLABLE_COUNT_LEGEND }}
                        </p>

                        <!-- How each incorrect sound went wrong. These used to
                             exist only in the Transcribe modal, which a
                             clinician reaches by choosing to mark phonetics, so
                             almost nothing was ever categorised however good
                             the schema was. Groups 4 and 5 score the number of
                             syllables, not sounds, so they get no categories -
                             the same rule the model follows. -->
                        <div class="sound__errors__box" v-if="showSoundErrors">
                            <div class="rlms-flex rlms-items-center rlms-gap-5">
                                <span class="rlms-fw-500 rlms-fs-xs">{{ SOUND_ERROR_PROMPT }}</span>
                                <Tooltip :content="`<p style='font-size: 14px; line-height: 18px;'>${SOUND_ERROR_LEGEND}</p>`"
                                    placement="right" width="320px">
                                    <template #button>
                                        <img :src="optionsInfoIcon" />
                                    </template>
                                </Tooltip>
                            </div>
                            <div class="sound__error__row" v-for="c in redConsonants" :key="`sound-error-${c.id}`"
                                :class="{ suggested__row: suggestedReasons[c.id] }">
                                <div class="tbh__labels__group sound__error__label">
                                    <span class="label red">{{ c.value }}</span>
                                </div>
                                <div class="options__group">
                                    <div class="options__item__box" v-for="option in SOUND_ERROR_KEYS" :key="option">
                                        <input type="radio" :id="`sound_error_${item?.id}_${c.id}_${option}`"
                                            :name="`sound_error_${item?.id}_${c.id}`" :value="option"
                                            v-model="incorrectReasons[c.id]" @change="setSoundError(c.id)"
                                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || !canConductAssessment" />
                                        <label :for="`sound_error_${item?.id}_${c.id}_${option}`"
                                            :title="soundErrorDefinition(option)">{{ soundErrorLabel(option) }}</label>
                                    </div>
                                </div>
                                <!-- Until it is confirmed it is the model's, not
                                     the clinician's: it is on screen, it is not
                                     saved as their answer, and one click makes
                                     it theirs. -->
                                <span class="suggested__tag rlms-fs-xxs" v-if="suggestedReasons[c.id]">
                                    {{ SOUND_ERROR_SUGGESTED_TAG }}
                                    <a href="javascript:void(0);" class="rlms-text-primary-400 rlms-fw-500"
                                        @click="confirmSoundError(c.id)"
                                        v-if="canConductAssessment && !['pending', 'processing'].includes(transcriptionStatus)">Confirm</a>
                                </span>
                            </div>
                            <!-- The model filled these in from the recording, so
                                 the clinician corrects rather than types. -->
                            <p class="note-text rlms-fs-xxs rlms-text-primary-400 rlms-mt-2" v-if="anySuggestedReason">
                                {{ SOUND_ERROR_SUGGESTION_NOTE }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Score Radio Buttons -->
                <div class="popup_point_items"
                    :class="{ disabled_tab: ['pending', 'processing'].includes(transcriptionStatus) === true }">
                    <span class="rlms-fw-500">Score:</span>
                    <div class="point__checkbox" @click="handleScoreClick('1.0')">
                        <input type="radio" :id="`audio_point_${item?.id}_1`" value="1.0" readonly class="markscore"
                            :checked="score === '1.0'"
                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || !canConductAssessment" />
                        <label :for="`audio_point_${item?.id}_1`" class="rlms-fs-xs">1 Point</label>
                    </div>
                    <div class="point__checkbox" v-if="group.id <= 3" @click="handleScoreClick('0.5')">
                        <input type="radio" :id="`audio_point_${item?.id}_2`" value="0.5" readonly class="markscore"
                            :checked="score === '0.5'"
                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || !canConductAssessment" />
                        <label :for="`audio_point_${item?.id}_2`" class="rlms-fs-xs">1/2 Point</label>
                    </div>
                    <div class="point__checkbox" readonly @click="handleScoreClick('0.0')">
                        <input type="radio" :id="`audio_point_${item?.id}_3`" value="0.0" class="markscore"
                            :checked="score === '0.0'"
                            :disabled="['pending', 'processing'].includes(transcriptionStatus) || !canConductAssessment" />
                        <label :for="`audio_point_${item?.id}_3`" class="rlms-fs-xs">0 Point</label>
                    </div>
                </div>
                <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2" v-if="group?.id">
                    {{ scoringRuleNote(group.id) }}
                </p>

                <!-- The app would have given this item the point and did not,
                     because the voice it heard may have been the examiner's.
                     Say which rule and why: the marks stand, the score is the
                     clinician's. -->
                <p class="note-text rlms-fs-xxs rlms-text-danger-300 rlms-mt-2" v-if="withheldMessage">
                    {{ withheldMessage }} {{ MARKS_STAND_NOTE }}
                </p>

                <!-- Placeholder Image -->
                <div class="transcription-setting-outer">
                    <div class="transcription-setting bg_grey rlms-w-100">
                        <label class="setting-label">
                            Transcription
                            <Tooltip :content="`<p style='font-size: 14px; line-height: 18px;'>
                                        This setting enables or disables automatic transcription of audio recordings.
                                        </p>`" placement="right" backgroundColor="transparent">
                                <template #button>
                                    <img :src="optionsInfoIcon" />
                                </template>
                            </Tooltip>
                            <span class="badge">Beta</span>
                        </label>
                        <div class="toggle-switch" :style="{
                            opacity: (agreement?.is_accepted && !legal.aiBlocked) ? 1 : 0.5,
                            pointerEvents: (agreement?.is_accepted && !legal.aiBlocked) ? 'auto' : 'none',
                        }">
                            <span class="rlms-fs-xs">Off</span>
                            <label class="switch">
                                <input type="checkbox" id="transcribe-settings" :checked="isTranscriptionEnabled"
                                    @change="updateTranscribeSetting($event)">
                                <span class="slider round"></span>
                            </label>
                            <span class="rlms-fs-xs">On</span>
                        </div>
                    </div>
                    <!-- A new version of the privacy policy or terms pauses the AI
                         features until it is accepted. Everything else on this
                         screen keeps working, including recording by hand. -->
                    <template v-if="legal.aiBlocked">
                        <p class="note-text rlms-fs-xxs rlms-text-center rlms-text-danger-300 rlms-mt-2">
                            Automatic transcription is paused until you accept the updated privacy policy and
                            terms. You can still record and score by hand.
                        </p>
                    </template>
                    <template v-else-if="!agreement?.is_accepted">
                        <template v-if="assessment?.learner?.is_authorized_user && !assessment?.learner?.is_administrator">
                            <p class="note-text rlms-fs-xxs rlms-text-center rlms-text-danger-300 rlms-mt-2">Please ask your organization to
                                accept the AI Usage Agreement before using AI features.</p>

                        </template>
                        <template v-else>
                            <p class="note-text rlms-fs-xxs rlms-text-center rlms-text-danger-300 rlms-mt-2">You must
                                accept the AI Usage Agreement before using AI features. <router-link
                                    :to="`/organizations/${organizationId}/agreements`"
                                    class="rlms-btn rlms-btn-outline-primary rlms-text-primary-400 rlms-fw-500 rlms-mt-3 rlms-mb-2">Click Here to Accept</router-link></p>
                        </template>
                    </template>
                </div>

                <!-- Waveform & Controls -->
                <div class="play__audio__box__wrapper">
                    <div class="recording__title rlms-flex">
                        <h6 class="title rlms-flex rlms-items-center rlms-self-start">Recording &nbsp;
                            <Tooltip :content="`<p style='font-size: 14px; line-height: 18px;'>
                                        <b>🎙️ Recording Instructions</b><br/>
                                        Max Record Limit is ${maxRecordingTime - 1} seconds.<br/>
                                        After recording, most of the clip is selected for you on the audio bar.<br/>
                                        • Only the selected range will be sent for AI transcription.<br/>
                                        • Use <b>Send the whole clip</b> if the response is near the start or the end.<br/>
                                        • The AI will automatically highlight consonants in your transcript for easier review.<br/>
                                        • Drag the handles on the audio bar to fine-tune your selection before confirming.<br/>
                                    </p>`" placement="right" width="380px">
                                <template #button>
                                    <img :src="optionsInfoIcon" />
                                </template>
                            </Tooltip>
                        </h6>
                        <div class="rlms-flex rlms-gap-10 rlms-items-center">
                            <!-- Control Buttons -->
                            <div class="mic__play__btn_with_timer">
                                <div class="button">
                                    <a href="javascript:void(0);" v-if="!hasRecording && recordingState === 'idle'"
                                        @click="startRecording">
                                        <img :src="recordIcon" />
                                    </a>
                                    <a href="javascript:void(0);" v-if="!hasRecording && recordingState === 'recording'"
                                        @click="stopRecording">
                                        <img :src="stopIcon" />
                                    </a>
                                    <a href="javascript:void(0);"
                                        v-if="hasRecording && recordingState === 'recorded' && !isPlaying"
                                        @click="togglePlayback">
                                        <img :src="playIcon" />
                                    </a>
                                    <a href="javascript:void(0);"
                                        v-if="hasRecording && recordingState === 'recorded' && isPlaying"
                                        @click="togglePlayback">
                                        <img :src="pauseIcon" />
                                    </a>
                                </div>
                                <div class="timer">
                                    {{ formattedTime }}
                                </div>
                            </div>
                            <div class="edit_audio_button" v-if="hasRecording && recordingState !== 'recording'">
                                <a href="javascript:void(0);" @click="resetRecording">
                                    <img :src="redoBtn" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2">
                        {{ RECORDING_SETUP_NOTE }}
                    </p>

                    <!-- This item had a recording and its audio file has gone.
                         Say so, rather than showing an empty recorder as
                         though nothing was ever recorded. -->
                    <p class="note-text rlms-fs-xxs rlms-text-danger-300 rlms-mt-2" v-if="unavailableDetail">
                        <b>{{ RECORDING_UNAVAILABLE_TITLE }}.</b> {{ unavailableDetail }} You can record this item
                        again.
                    </p>

                    <div ref="waveformContainer" class="waveform-container" style="width: 100%;"
                        v-show="hasRecording || recordingState === 'recording'"></div>

                    <!-- Where the app listened. The amber region is drawn over
                         the clinician's own purple selection, so a response the
                         app found outside the part they sent is visible rather
                         than silently credited. -->
                    <template v-if="hasRecording">
                        <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2" v-if="heardWindowLine">
                            <span class="heard-window-swatch" :style="{ backgroundColor: HEARD_WINDOW_COLOR }"></span>
                            {{ heardWindowLine }} {{ HEARD_WINDOW_LEGEND }}
                        </p>
                        <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2" v-else-if="heardNothingLine">
                            {{ heardNothingLine }}
                        </p>
                    </template>

                    <!-- Which part of the clip gets transcribed -->
                    <div class="audio-selection-row rlms-flex rlms-items-center rlms-gap-10 rlms-mt-2"
                        v-if="canChangeSelection">
                        <a href="javascript:void(0);"
                            class="rlms-btn rlms-btn-outline-primary rlms-text-primary-400 rlms-fw-500"
                            @click="selectWholeClip" v-if="!isWholeClipSelected">Send the whole clip</a>
                        <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mb-0">
                            {{ selectionLabel }} Drag the handles on the audio bar to change it.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Footer -->
        <template #footer>
            <div class="audio-nav">
                <a href="javascript:void(0)" class="prev" @click="goTo('prev')" :style="{
                    opacity: recordingState !== 'recording' ? 1 : 0.5,
                    pointerEvents: recordingState !== 'recording' ? 'auto' : 'none',
                    cursor: recordingState !== 'recording' ? 'pointer' : 'not-allowed'
                }">
                    <span class="btn__icon">
                        <img src="@/assets/images/arrow-left.png">
                    </span>
                    <span class="btn__text">Prev</span>
                </a>
                <div class="purple__solid__btn" v-if="audioBlob">
                    <a href="javascript:void(0)" id="saveAndClose" @click="saveAudio()" :style="{
                        opacity: audioBlob ? 1 : 0.5,
                        pointerEvents: audioBlob ? 'auto' : 'none',
                        cursor: audioBlob ? 'pointer' : 'not-allowed'
                    }">
                        Save
                    </a>
                </div>
                <a href="javascript:void(0)" class="next" @click="goTo('next')" :style="{
                    opacity: recordingState !== 'recording' ? 1 : 0.5,
                    pointerEvents: recordingState !== 'recording' ? 'auto' : 'none',
                    cursor: recordingState !== 'recording' ? 'pointer' : 'not-allowed'
                }">
                    <span class="btn__text">Next</span>
                    <span class="btn__icon">
                        <img src="@/assets/images/arrow-right1.png">
                    </span>
                </a>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import BaseModal from '../BaseModal.vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';
import { showConfirmDialog, showDialog, showToast } from '@/utils/swal';
import { useLoaderStore } from '@/stores/loader';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import redoBtn from '@/assets/images/rotate-left.png';
import recordIcon from '@/assets/images/audio/record.png';
import stopIcon from '@/assets/images/audio/stop.png';
import playIcon from '@/assets/images/audio/play.png';
import pauseIcon from '@/assets/images/audio/pause.png';
import { useAuthStore } from '@/stores/auth';
import optionsInfoIcon from '@/assets/images/info-circle 2.png';
import optionsInfoIcon2 from '@/assets/images/info-circle.png';
import { authrizedPermissions } from '@/utils/permissions';
import Tooltip from '../Tooltip.vue';
import aiImg from '@/assets/images/ai-transcription-img.png';
import TranscriptionFeedbackAction from './TranscriptionFeedbackAction.vue';
import { convertToWav } from '@/utils/audioToWav';
import { PHONEME_COLOUR_LEGEND, RECORDING_SETUP_NOTE, SYLLABLE_COUNT_LEGEND, scoringRuleNote } from '@/utils/assessment-notes';
import { RECORDING_UNAVAILABLE_TITLE, recordingUnavailableDetail } from '@/utils/recording-retention';
import {
    HEARD_WINDOW_COLOR,
    HEARD_WINDOW_LEGEND,
    HEARD_WINDOW_REGION_ID,
    MARKS_STAND_NOTE,
    NOTHING_FOUND_NOTE,
    SELECTION_COLOR,
    creditWithheldMessage,
    foundNothing,
    heardRegion,
    heardWindow,
    heardWindowNote,
} from '@/utils/sound-window';
import { aiFeaturesAllowed } from '@/utils/legal-gate';
import {
    SOUND_ERROR_KEYS,
    SOUND_ERROR_LEGEND,
    SOUND_ERROR_PROMPT,
    SOUND_ERROR_SUGGESTED_TAG,
    SOUND_ERROR_SUGGESTION_NOTE,
    incorrectDetailsSource,
    incorrectDetailsToSave,
    parseIncorrectDetails,
    soundErrorDefinition,
    soundErrorLabel,
} from '@/utils/sound-error-categories';
import { useLegalStore } from '@/stores/legal';

const isTranscriptionEnabled = ref(false);

const authStore = useAuthStore();
const emit = defineEmits(['close', 'saved', 'goto', 'scored', 'feedback']);

const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    maxRecordingTime: { type: Number, default: 31 },
    item: Object,
    canConductAssessment: Boolean,
    assessment: Object,
    group: Object,
    agreement: { type: Object, default: null },
});

const title = 'Record Audio';
const waveformContainer = ref(null);
const wavesurfer = ref(null);
const regions = RegionsPlugin.create();
const wavesurferRecord = ref(null);
const audioUrl = ref('');
const audioBlob = ref(null);
const audioDuration = ref(null);
const recordingState = ref('idle');
const hasRecording = ref(false);
const isPlaying = ref(false);
const timeElapsed = ref(0);
let timerInterval = null;
const audioStartTime = ref(null);
const audioEndTime = ref(null);
const noResponse = ref(false);
const transcriptionStatus = ref(null);

// The second, read-only region: the stretch of the clip the app says it heard
// the response in. It is not the selection, so the rule that keeps exactly one
// selection region has to leave it alone.
const isHeardWindow = (region) => region?.id === HEARD_WINDOW_REGION_ID;

// What the app listened to, and why it would not score the item itself. Both
// are absent until the backend's whole-clip search has run on this item, and
// then the screen reads exactly as it did before.
const heardWindowLine = computed(() => heardWindowNote(props.item));
const heardNothingLine = computed(() => (foundNothing(props.item) ? NOTHING_FOUND_NOTE : null));
const withheldMessage = computed(() => creditWithheldMessage(props.item));

// Set only while this item's audio file is gone: it clears the moment
// something is recorded again, because that reads the item's own `audio`.
const unavailableDetail = computed(() => recordingUnavailableDetail(props.item));

// The part of the clip sent for transcription. A narrow default window cut off
// the start or the end of short responses, so the default is forgiving: most of
// the clip, and never less than this many seconds.
const DEFAULT_SELECTION_MIN_SECONDS = 4;
const DEFAULT_SELECTION_RATIO = 0.8;

const defaultSelection = (duration) => {
    const clip = Number(duration) || 0;
    const width = Math.max(DEFAULT_SELECTION_MIN_SECONDS, clip * DEFAULT_SELECTION_RATIO);

    if (width >= clip) return { start: 0, end: clip };

    const mid = clip / 2;
    return {
        start: Math.max(0, mid - width / 2),
        end: Math.min(clip, mid + width / 2),
    };
};

// A selection already saved with the recording, which is not editable.
const hasSavedSelection = () =>
    audioStartTime.value !== null && audioStartTime.value !== undefined && audioStartTime.value !== ''
    && audioEndTime.value !== null && audioEndTime.value !== undefined && audioEndTime.value !== '';

// The selection can only be changed before the recording is saved.
const canChangeSelection = computed(() =>
    !!audioBlob.value && recordingState.value === 'recorded'
    && !['pending', 'processing'].includes(transcriptionStatus.value)
);

const isWholeClipSelected = computed(() => {
    const clip = Number(audioDuration.value) || 0;
    if (!clip) return false;

    return Number(audioStartTime.value) <= 0.05 && Number(audioEndTime.value) >= clip - 0.05;
});

const selectionLabel = computed(() => {
    if (audioStartTime.value === null || audioEndTime.value === null) return '';

    const start = Number(audioStartTime.value);
    const end = Number(audioEndTime.value);
    if (!isFinite(start) || !isFinite(end)) return '';

    return `Sending ${(end - start).toFixed(1)}s of this recording (${start.toFixed(1)}s to ${end.toFixed(1)}s).`;
});

// Send the whole clip: one click instead of dragging the handles to the ends.
const selectWholeClip = () => {
    if (!wavesurfer.value) return;

    const duration = wavesurfer.value.getDuration();
    if (!duration) return;

    // Only the selection goes: the window the app heard is a record of what
    // happened, not something this button changes.
    regions.getRegions().filter(region => !isHeardWindow(region)).forEach(region => region.remove());

    regions.addRegion({
        start: 0,
        end: duration,
        color: SELECTION_COLOR,
        drag: true,
        resize: true,
    });
};

// Reactive references to item properties
const score = computed({
    get: () => props.item?.score,
    set: (val) => {
        emit('scored', { type: 'score', id: props.item?.id, value: val });
    }
});

const updateScore = (value) => {
    score.value = (score.value === value) ? null : value;
};

function handleScoreClick(value) {
    if (['pending', 'processing'].includes(transcriptionStatus.value)) {
        return;
    }

    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = props?.assessment?.learner;

        if (learner?.is_administrator) {
            message = `You don’t have permission to score this assessment because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to score this assessment because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    updateScore(value);
}

const consonantScore = ref([]);
const consonantStates = ref([]);

// One category per sound slot, parallel to the sounds: the clinician's own
// choices, the model's suggestions, and which slots are still only a suggestion.
const incorrectReasons = ref([]);
const aiReasons = ref([]);
const suggestedReasons = ref([]);

const redConsonants = computed(() => consonantStates.value.filter(c => c.state === 'red'));

// Only groups 1-3 show the sounds as labels a category can hang off; groups 4
// and 5 tick syllables and score the count, so they get no categories.
const showSoundErrors = computed(
    () => !!props.item?.consonant && !noResponse.value && redConsonants.value.length > 0
);

const anySuggestedReason = computed(() => redConsonants.value.some(c => suggestedReasons.value[c.id]));

const loader = useLoaderStore();
const legal = useLegalStore();

const formattedTime = computed(() => {
    const min = Math.floor(timeElapsed.value / 60);
    const sec = timeElapsed.value % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
});

onMounted(async () => {
    await fetchUser();
});

onBeforeUnmount(() => {
    clearTimer();
    if (wavesurfer.value) wavesurfer.value.pause();
    if (audioUrl.value && !props.audio) URL.revokeObjectURL(audioUrl.value);
});

const closeModal = () => emit('close');

// Draw the stretch of the clip the app says it heard the response in, over the
// clinician's own selection and in a different colour. Read-only: this is a
// record of where the app listened, and the clinician overrules it with the
// score, not by dragging it. Safe to call at any time — it replaces whatever
// window was drawn before, and draws nothing when no search has run.
const drawHeardWindow = () => {
    if (!wavesurfer.value) return;

    regions.getRegions().filter(isHeardWindow).forEach(region => region.remove());

    const heard = heardRegion(props.item, wavesurfer.value.getDuration());
    if (!heard) return;

    regions.addRegion({
        id: HEARD_WINDOW_REGION_ID,
        start: heard.start,
        end: heard.end,
        color: HEARD_WINDOW_COLOR,
        drag: false,
        resize: false,
    });
};

const initWaveSurfer = (mode = 'playback') => {
    if (wavesurfer.value) wavesurfer.value.pause();
    if (wavesurfer.value) wavesurfer.value.destroy();

    wavesurfer.value = WaveSurfer.create({
        normalize: true,
        barWidth: 3,
        barGap: 1,
        barRadius: 1,
        height: 80,
        cursorWidth: 2,
        container: waveformContainer.value,
        backend: 'WebAudio',
        progressColor: 'rgba(114,92,255,1)',
        waveColor: 'rgba(210,199,254,1)',
        cursorColor: "#4a74a5",
        plugins: [regions]
    });

    if (mode === 'recording') {
        wavesurferRecord.value = wavesurfer.value.registerPlugin(RecordPlugin.create({
            renderRecordedAudio: true,
            scrollingWaveform: true,
            continuousWaveform: false,
        }));

        wavesurferRecord.value.on('record-end', async (blob) => {
            const wavBlob = await convertToWav(blob);
            audioBlob.value = wavBlob;
            audioUrl.value = URL.createObjectURL(wavBlob);

            hasRecording.value = true;
            recordingState.value = 'recorded';

            initWaveSurfer('playback');

            // audioBlob.value = blob;
            // audioUrl.value = URL.createObjectURL(blob);
            // hasRecording.value = true;
            // recordingState.value = 'recorded';
            // initWaveSurfer('playback');
        });

        wavesurferRecord.value.on('record-progress', (ms) => {
            timeElapsed.value = Math.floor(ms / 1000);
            if (timeElapsed.value >= props.maxRecordingTime) stopRecording();
        });

        wavesurferRecord.value.startRecording().catch((err) => {
            showDialog({ title: err?.message ?? 'Microphone access was denied or not available.' });
            resetRecording();
        });
    } else {
        loader.show();
        wavesurfer.value.load(audioUrl.value);

        wavesurfer.value.on('ready', () => {
            audioDuration.value = Math.floor(wavesurfer.value.getDuration());
            timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
            loader.hide();
        });
        wavesurfer.value.on('play', () => { if (!isPlaying.value) timeElapsed.value = 0; });

        wavesurfer.value.on('audioprocess', (currentTime) => {
            if (currentTime <= Math.floor(wavesurfer.value.getDuration())) {
                timeElapsed.value = Math.floor(currentTime);
            } else {
                setTimeout(() => {
                    wavesurfer.value.stop();
                    isPlaying.value = false;
                    timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
                }, 500);
            }
        });

        wavesurfer.value.on('finish', () => {
            isPlaying.value = false;
            timeElapsed.value = Math.floor(wavesurfer.value.getDuration());
        });

        wavesurfer.value.on('decode', () => {
            if (hasSavedSelection()) {
                regions.addRegion({
                    start: Number(audioStartTime.value),
                    end: Number(audioEndTime.value),
                    color: SELECTION_COLOR,
                    drag: false,
                    resize: false,
                });
            } else {
                const { start, end } = defaultSelection(wavesurfer.value.getDuration());

                regions.addRegion({
                    start,
                    end,
                    color: SELECTION_COLOR,
                    drag: true,
                    resize: true,
                });
            }

            drawHeardWindow();
        })

        let loop = false

        {
            let activeRegion = null
            regions.on('region-in', () => {
                // activeRegion = region
                isPlaying.value = true
            })
            regions.on('region-out', (region) => {
                if (activeRegion === region) {
                    if (loop) {
                        region.play()
                    } else {
                        activeRegion = null
                        isPlaying.value = false;
                    }
                }
            })
            regions.on('region-clicked', (region, e) => {
                e.stopPropagation() // prevent triggering a click on the waveform
                activeRegion = region
                region.play(true);
                // Clicking the window the app heard plays it, which is the
                // point of drawing it — but it keeps its own colour.
                if (!isHeardWindow(region)) {
                    region.setOptions({ color: SELECTION_COLOR })
                }
            })

            // Reset the active region when the user clicks anywhere in the waveform
            wavesurfer.value.on('interaction', () => {
                activeRegion = null
            });

            // The window the app heard is read-only and is never the selection,
            // so it is exempt from both rules below: it neither clears the
            // selection nor becomes the range that gets sent.
            regions.on('region-created', (region) => {
                if (isHeardWindow(region)) return;

                const allRegions = regions.getRegions();

                if (allRegions.length > 1) {
                    // Remove the old regions, keep only the latest one
                    allRegions
                        .filter(r => r.id !== region.id && !isHeardWindow(r))
                        .forEach(oldRegion => oldRegion.remove());
                }

                console.log('Selected range:', region.start, region.end);

                audioStartTime.value = region.start;
                audioEndTime.value = region.end;
            });

            regions.on('region-updated', (region) => {
                if (isHeardWindow(region)) return;

                const allRegions = regions.getRegions();

                if (allRegions.length > 1) {
                    // Remove the old regions, keep only the latest one
                    allRegions
                        .filter(r => r.id !== region.id && !isHeardWindow(r))
                        .forEach(oldRegion => oldRegion.remove());
                }

                console.log('Selected range updated:', region.start, region.end);

                audioStartTime.value = region.start;
                audioEndTime.value = region.end;
            });
        }

    }
};

const startRecording = () => {
    timeElapsed.value = 0;
    recordingState.value = 'recording';
    initWaveSurfer('recording');
};

const stopRecording = () => {
    if (recordingState.value === 'recording' && wavesurferRecord.value?.isRecording()) {
        wavesurferRecord.value.stopRecording();
        clearTimer();
        recordingState.value = 'recorded';
    }
};

const togglePlayback = () => {
    if (!wavesurfer.value) return;
    isPlaying.value ? wavesurfer.value.pause() : wavesurfer.value.play();
    isPlaying.value = !isPlaying.value;
};

const resetRecording = () => {
    wavesurfer.value?.pause();
    wavesurfer.value?.destroy();
    if (audioUrl.value && !props.audio) URL.revokeObjectURL(audioUrl.value);

    audioUrl.value = '';
    audioBlob.value = null;
    hasRecording.value = false;
    isPlaying.value = false;
    recordingState.value = 'idle';
    timeElapsed.value = 0;
    audioStartTime.value = null;
    audioEndTime.value = null;
};

const clearTimer = () => {
    if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
    }
};

const saveAudio = async () => {
    try {
        if (audioStartTime.value === null || audioEndTime.value === null) {
            showDialog({
                icon: 'warning',
                html: 'Please select the audio range for transcription'
            });

            return;
        }

        loader.show();
        const formData = new FormData();
        formData.append('attemptid', props.item?.id);
        if (audioBlob.value) {
            // const cleanBlob = new Blob([audioBlob.value], { type: 'audio/webm' });
            const cleanBlob = new Blob([audioBlob.value], { type: 'audio/wav' });
            formData.append('audio', cleanBlob, 'audio.wav');
        }

        formData.append('duration', audioDuration.value);
        formData.append('start_time', audioStartTime.value);
        formData.append('end_time', audioEndTime.value);

        const { data } = await api.post(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}/attempt`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        emit('saved', {
            type: 'audio',
            id: props.item?.id,
            value: data?.data?.audio,
            extra: {
                duration: data?.data?.duration,
                start_time: data?.data?.start_time,
                end_time: data?.data?.end_time,
                transcription_status: data?.data?.transcription_status
            }
        });

        audioBlob.value = null;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const goTo = async (direction = 'next') => {
    if (audioBlob.value) {
        const capitalizedDirection = direction.charAt(0).toUpperCase() + direction.slice(1);

        const result = await showConfirmDialog({
            title: 'Are you sure?',
            html: '<p id="text-message">Are you sure?</p><span class="rlms-text-danger-500" id="delete-confirm"> You have an unsaved audio recording.</span>',
            showCancelButton: true,
            confirmButtonColor: '#725cff',
            cancelButtonColor: '#725cff',
            confirmButtonText: `Save & ${capitalizedDirection}`,
            cancelButtonText: 'Skip',
            confirmButtonClass: 'rlms-btn rlms-btn-primary',
            cancelButtonClass: 'rlms-btn rlms-btn-default',
        });

        if (result.isConfirmed) {
            await saveAudio();
        }
    }

    // closeModal();
    if (direction === 'next') {
        emit('goto', { id: props.item?.id, direction });
    } else if (direction === 'prev') {
        emit('goto', { id: props.item?.id, direction });
    }

    resetRecording();
};

const fetchAudioBlobUrl = async (url) => {
    if (!url) return null;

    try {
        loader.show();
        const token = useAuthStore().token;
        const response = await api.get(url, {
            responseType: "blob",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return URL.createObjectURL(response.data);
    } catch (error) {
        handleError(error);

        return null;
    } finally {
        loader.hide();
    }
};

const updateTranscribeSetting = async (event) => {
    try {
        // A new version of the privacy policy or terms pauses the AI features
        // until it is accepted; everything else on this screen keeps working.
        if (!aiFeaturesAllowed()) {
            event.target.checked = false;

            return;
        }

        if (!props?.agreement?.is_accepted) {
            event.target.checked = false;
            showToast({
                icon: 'warning',
                title: 'Warning! ',
                text: 'You must accept the AI Usage Agreement before using AI features.',
            });

            return;
        }

        loader.show();

        await api.patch('/user/update', {
            automated_transcribe_enabled: event.target.checked
        });

        showToast({
            icon: 'success',
            title: 'Success! ',
            text: 'Transcribe setting updated successfully.',
        });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const fetchUser = async () => {
    try {
        loader.show();

        await authStore.fetchUser();
        const user = authStore?.userinfo;

        if(props.agreement?.is_accepted) {
            isTranscriptionEnabled.value = user.automated_transcribe_enabled;
        }
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

// A category belongs to a sound marked incorrect, which is the only place one is
// on screen. So a suggestion fills a red sound with no answer yet - including
// later, when the transcription lands - and a sound that is not red carries
// none: a value nobody can see must never be saved as the clinician's choice.
const syncSoundErrors = () => {
    consonantStates.value.forEach((c, i) => {
        if (c.state !== 'red') {
            incorrectReasons.value[i] = null;
            suggestedReasons.value[i] = false;

            return;
        }

        if (!incorrectReasons.value[i] && aiReasons.value[i]) {
            incorrectReasons.value[i] = aiReasons.value[i];
            suggestedReasons.value[i] = true;
        }
    });
};

const loadSoundErrors = (item, length, isNewItem) => {
    const stored = parseIncorrectDetails(item?.incorrect_details);
    aiReasons.value = parseIncorrectDetails(item?.ai_incorrect_details);

    // Within one item these choices are the newest thing there is: each is saved
    // as it is made, while the item prop can still carry the payload the
    // recorder was opened with. So only a different item reloads them.
    if (isNewItem || incorrectReasons.value.length !== length) {
        incorrectReasons.value = Array.from({ length }, (_, i) => stored[i] ?? null);
        suggestedReasons.value = Array.from({ length }, () => false);
    }

    syncSoundErrors();
};

// Watch item and initialize state
watch(() => props.item, async (newItem, oldItem) => {
    noResponse.value = newItem?.no_response === 'Y';
    transcriptionStatus.value = newItem?.transcription_status;

    // Parse consonants
    let parsedConsonants = [];
    try {
        if (newItem?.consonant) {
            parsedConsonants = JSON.parse(newItem?.consonant?.replace(/'/g, '"')) || [];
        } else {
            parsedConsonants = newItem?.question.split('-');
        }
    } catch {
        parsedConsonants = [];
    }

    let parsedConsonantScore = [];
    parsedConsonantScore = typeof newItem?.consonant_score === 'string'
        ? JSON.parse(newItem.consonant_score)
        : newItem?.consonant_score || [];

    // Map to state
    consonantStates.value = parsedConsonants.map((value, index) => {
        // Every slot starts unscored, including groups 4 and 5 (which have no
        // consonant list): a slot is only red once a 0 has actually been recorded.
        let state = 'default';
        const score = parsedConsonantScore?.[index];

        if (value === '-' || value === ' ') state = 'green';
        else if (score == 1 || score === true) state = 'green';
        else if (score == 0 || score === false) state = 'red';
        return { id: index, value, state };
    });

    loadSoundErrors(newItem, parsedConsonants.length, newItem?.id !== oldItem?.id);

    if (newItem?.audio !== oldItem?.audio) {
        audioUrl.value = await fetchAudioBlobUrl(newItem.audio);
    }

    audioStartTime.value = newItem?.start_time;
    audioEndTime.value = newItem?.end_time;

    if (audioUrl.value) {
        hasRecording.value = true;
        recordingState.value = 'recorded';
        initWaveSurfer('playback');
    } else {
        resetRecording();
    }
}, { immediate: true, deep: true });

// The transcription can land while this item is open, so the window the app
// heard can arrive after the waveform was already drawn.
watch(() => JSON.stringify(heardWindow(props.item) ?? null), () => drawHeardWindow());

// Assign class based on consonant state
const getClassForState = (state) => {
    if (state === 'green') return 'green';
    if (state === 'red') return 'red';
    return 'purple';
};

// Toggle consonant state
const toggleConsonant = async (index) => {
    const current = consonantStates.value[index].state;
    consonantStates.value[index].state =
        current === 'default' || current === 'red' ? 'green' : 'red';

    syncSoundErrors();

    await saveTranscribe();
};

// A category the clinician picked themselves is theirs, not a suggestion, and
// it is saved the moment it is picked - the recorder has no Save for the marks.
const setSoundError = async (index) => {
    suggestedReasons.value[index] = false;

    await saveTranscribe();
};

// Confirming the model's suggestion as it stands. Picking the radio that is
// already selected fires no change event, so agreeing needs a control of its
// own - without one, agreement is the one answer a clinician cannot give.
const confirmSoundError = async (index) => {
    if (!incorrectReasons.value[index]) return;

    suggestedReasons.value[index] = false;

    await saveTranscribe();
};

// Handle saving transcription data
const saveTranscribe = async () => {
    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = props.assessment?.learner;

        if (learner?.is_administrator) {
            message = `You don’t have permission to score this assessment because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to score this assessment because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    // if (anyDefault.value && !noResponse.value) {
    //     showDialog({ icon: 'warning', html: 'Please mark the phonetics' });
    //     return;
    // }

    try {
        loader.show();

        consonantStates.value.forEach((item, index) => {
            consonantScore.value[index] = item.state === 'green' ? 1 : item.state === 'red' ? 0 : null;
        });

        const payload = {
            attemptid: props.item.id,
            consonant_score: consonantScore.value,
            no_response: noResponse.value ? 'Y' : 'N',
        };

        // Groups 4 and 5 have no sounds to categorise and never show a chip, so
        // the recorder does not send their categories: it would only overwrite
        // whatever the Transcribe modal holds with nothing.
        //
        // `incorrect_details` carries the clinician's own answers only, so a
        // suggestion they never confirmed can never be counted as a label; the
        // source of each sound goes alongside it.
        if (props.item?.consonant) {
            payload.incorrect_details = noResponse.value
                ? null
                : incorrectDetailsToSave(incorrectReasons.value, suggestedReasons.value);
            payload.incorrect_details_source = noResponse.value
                ? null
                : incorrectDetailsSource(incorrectReasons.value, suggestedReasons.value);
        }

        const { data } = await api.post(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}/attempt`,
            payload
        );

        showToast({
            icon: 'success',
            title: 'Successful',
            text: 'Transcribe details saved successfully',
        });

        transcriptionStatus.value = data?.data?.transcription_status;

    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const transcriptionMessage = computed(() => {
    if (transcriptionStatus.value != 'manual') {
        switch (transcriptionStatus.value) {
            case "processing":
            case "pending":
                return `<p class="rlms-text-primary-400" style="font-size:14px;line-height:18px;">
                  Your transcription is currently in progress. Please wait and check back later.
                </p>`;
            case "completed":
                return `<p class="rlms-text-primary-400" style="font-size:14px;line-height:18px;">
                  The transcription from the audio was automatically generated and may be incorrect. You can report any errors by clicking the dislike icon and verify it manually <br/> Alternatively, click the like icon if you’re satisfied with the AI response. Your feedback is valuable to us.
                </p>
                `;
            case "failed":
                return `<p class="rlms-text-danger-400" style="font-size:14px;line-height:18px;">
                  Automated Transcription failed. Please try again or contact support.
                </p>`;
            default:
                return "";
        }
    } else {
        return `<p class="rlms-text-primary-400" style="font-size:14px;line-height:18px;">
              You have manually overridden the transcription.
            </p>`;
    }
});

const handleFeedbackEvent = (data) => {
    emit('feedback', { attemptId: props.item.id, feedback: data });
};
</script>

<style scoped>
.sound__errors__box {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #ECE9FF;
}

.sound__error__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 8px;
}

.sound__error__row .sound__error__label {
    align-self: center;
    min-width: 42px;
}

.sound__error__row .options__group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
}

.sound__error__row .options__item__box {
    font-size: 13px;
}

/* An unconfirmed suggestion looks like one: it is the model's answer sitting on
   screen, not something already recorded as the clinician's. Purple rather than
   amber on purpose — amber on this screen is the stretch of the clip the app
   heard, and one colour must not mean two things. */
.sound__error__row.suggested__row {
    border-left: 2px dashed #B9AEFF;
    padding-left: 8px;
}

.suggested__tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #725CFF;
    font-style: italic;
}

.suggested__tag a {
    font-style: normal;
    text-decoration: underline;
}

/* Ties the line to the region drawn on the waveform; the colour itself comes
   from the one place both screens read it from. */
.heard-window-swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 5px;
    vertical-align: baseline;
}
</style>
